<!--
  DecisionsInboxPage — /issues/ — the "قرارات مطلوبة" hub.

  TWO lists off ONE fetch (interaction-v2's `GET /item-issues`, scoped server-side
  to the member's own boxes — see useDecisionsInbox for why this replaced the old
  1+N per-box derivation): the decisions still waiting on the member, and below
  them «قرارات سابقة» — the ones they already answered or that are finished. Every
  row deep-links to the same /issues/:id/ detail the notification opens, which now
  renders in every issue state.

  EAGER `component:` + requireMembership (a member refreshing/bookmarking the hub
  makes it the cold-boot URL — playbook Section 5 row 16). Re-reads on every entry so a
  just-answered decision moves from the top list to the history and the warm empty
  state closes the loop.

  `:page-content="false"` on <f7-page>: the page brings its OWN <f7-page-content>
  (for pull-to-refresh). Without the prop <f7-page> wraps that one in a second
  .page-content and both get the navbar's padding, so everything starts one
  navbar-height too low (a blank band under the navbar; measured 76px).
-->
<template>
  <f7-page name="decisions-inbox" :page-content="false" @page:beforein="onBeforeIn">
    <!-- `back-link-show-text` is required for the «رجوع» label to render at
         all: F7's nav-left drops the label unless it is set, leaving a bare
         chevron (nav-left.js — `backLink !== true && backLinkShowText`). -->
    <f7-navbar :back-link="t('general.back')" back-link-show-text>
      <f7-nav-title>
        <span class="inbox-title">
          {{ t('collection.inbox.header') }}
          <span
            v-if="inbox.count.value > 0"
            class="inbox-title__count"
            aria-live="polite"
          >{{ inbox.count.value }}</span>
        </span>
      </f7-nav-title>
    </f7-navbar>

    <f7-page-content ptr ptr-mousewheel @ptr:refresh="onPtr">
      <div class="inbox__wrap">
        <!-- LOADING — first scan only (re-entry refreshes in place). -->
        <div v-if="showSkeleton" class="inbox__skeleton" aria-hidden="true">
          <div v-for="n in 3" :key="n" class="inbox-skel-row">
            <f7-skeleton-block class="inbox-skel-row__thumb skeleton-effect-wave"></f7-skeleton-block>
            <div class="inbox-skel-row__body">
              <f7-skeleton-block class="inbox-skel-row__line inbox-skel-row__line--name skeleton-effect-wave"></f7-skeleton-block>
              <f7-skeleton-block class="inbox-skel-row__line inbox-skel-row__line--pill skeleton-effect-wave"></f7-skeleton-block>
            </div>
          </div>
        </div>

        <!-- ERROR — load failed with nothing to show. Retryable. -->
        <div v-else-if="showError" class="inbox__state">
          <f7-icon f7="wifi_exclamationmark" size="46" color="orange"></f7-icon>
          <p class="inbox__state-msg">{{ t('common.loadError') }}</p>
          <f7-button large outline class="inbox__retry" @click="inbox.load()">
            {{ t('general.retry') }}
          </f7-button>
        </div>

        <!-- LISTS — the actionable one, then the history. Both can be present, so
             neither is an `v-else` of the other: a member with nothing waiting
             still gets the warm "all caught up" close AND their past decisions. -->
        <template v-else>
          <!-- WAITING ON YOU -->
          <template v-if="inbox.count.value > 0">
            <p class="inbox__subtitle">{{ t('collection.inbox.subtitle') }}</p>
            <ul class="inbox__list">
              <li
                v-for="(row, index) in inbox.rows.value"
                :key="row.issueId"
                class="inbox__li"
                :style="staggerStyle(index)"
              >
                <issue-row :row="row" @select="open"></issue-row>
              </li>
            </ul>
          </template>

          <!-- EMPTY — the warm "all caught up" close. -->
          <div v-else class="inbox__state inbox__empty">
            <div class="inbox__empty-icon" aria-hidden="true">
              <f7-icon f7="checkmark_seal_fill" size="40"></f7-icon>
            </div>
            <p class="inbox__empty-msg">{{ t('collection.inbox.empty') }}</p>
          </div>

          <!-- PAST DECISIONS — answered (leader's court) + finished. The old
               derived scan could never show these: `activeIssue` goes null the
               moment an issue resolves. -->
          <template v-if="inbox.hasHistory.value">
            <h2 class="inbox__section-title">{{ t('collection.history.title') }}</h2>
            <ul class="inbox__list">
              <li v-for="row in inbox.historyRows.value" :key="row.issueId" class="inbox__li">
                <issue-row :row="row" @select="open"></issue-row>
              </li>
            </ul>
          </template>
        </template>
      </div>
    </f7-page-content>
  </f7-page>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import IssueRow from '@/components/collection/IssueRow.vue';
import { useDecisionsInbox } from '@/composables/useDecisionsInbox.js';
import { issueRoute } from '@/composables/useIssueNavigation.js';

const props = defineProps({
  f7route: { type: Object, default: null },
  f7router: { type: Object, default: null },
});

const { t } = useI18n({ useScope: 'global' });
const inbox = useDecisionsInbox();

let mounted = false;

const showSkeleton = computed(() => inbox.loading.value && !inbox.loaded.value);
// Only an error with NOTHING to show takes over the screen — a failed refresh
// behind a populated list leaves the list.
const showError = computed(() => !!inbox.error.value && !inbox.loaded.value);

onMounted(() => {
  mounted = true;
  inbox.load();
});

// Re-read on RE-entry (returning from a card the member just answered) — not on
// the first beforein, which coincides with the mount fetch above.
function onBeforeIn() {
  if (mounted && inbox.loaded.value) inbox.load();
}

function onPtr(done) {
  inbox.refresh(done);
}

// Page-level navigation uses the page's OWN injected router, but the path comes
// from useIssueNavigation so the route is spelt in exactly one file.
function open(row) {
  // Guard the id here as well as in the builder's sibling openIssue: issueRoute
  // is a pure string builder, so an idless row would otherwise navigate to a
  // path with the literal "null" in it and dead-end on a 404.
  if (row?.issueId == null) return;
  props.f7router?.navigate(issueRoute(row.issueId));
}

// Cap the entrance stagger so a long list doesn't crawl; reduced-motion disables
// it entirely (CSS).
function staggerStyle(index) {
  return { '--stagger': `${Math.min(index, 8) * 45}ms` };
}
</script>

<style scoped>
.inbox__wrap {
  max-width: 560px;
  margin: 0 auto;
  padding: 12px 14px 32px;
}

.inbox-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.inbox-title__count {
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: var(--poolit-radius-full, 999px);
  background: var(--ci-await-fg, #b45309);
  color: #fff;
  font-size: var(--poolit-font-size-small, 0.75rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  display: inline-grid;
  place-items: center;
  line-height: 1;
}

.inbox__subtitle {
  margin: 4px 2px 14px;
  font-size: var(--poolit-font-size-small, 0.85rem);
  line-height: 1.6;
  color: var(--poolit-text-muted, #66746d);
}

.inbox__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.inbox__li {
  animation: inbox-row-in 320ms var(--poolit-transition-ease, ease) both;
  animation-delay: var(--stagger, 0ms);
}

@keyframes inbox-row-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Section heading for the past-decisions list. */
.inbox__section-title {
  margin: 26px 2px 12px;
  font-size: var(--poolit-font-size-body, 0.95rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--poolit-text-primary, #15201c);
}

/* Shared state blocks (loading/error/empty) */
.inbox__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 56px 24px;
}

.inbox__state-msg {
  margin: 0;
  color: var(--poolit-text-secondary, #3d4a44);
  font-size: var(--poolit-font-size-body, 0.95rem);
  line-height: 1.6;
}

.inbox__retry {
  min-width: 180px;
}

.inbox__empty-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--ci-ok-bg, #e1f3e8);
  color: var(--ci-ok-fg, #16964f);
}

.inbox__empty-msg {
  margin: 0;
  max-width: 32ch;
  font-size: var(--poolit-font-size-body, 1rem);
  line-height: 1.7;
  color: var(--poolit-text-secondary, #3d4a44);
  font-weight: var(--poolit-font-weight-medium, 500);
}

/* Skeleton rows */
.inbox__skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
}

.inbox-skel-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--ci-border, #dce4e0);
  border-radius: var(--poolit-radius-lg, 16px);
}

.inbox-skel-row__thumb {
  width: 46px;
  height: 46px;
  border-radius: var(--poolit-radius-md, 12px);
  flex-shrink: 0;
}

.inbox-skel-row__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inbox-skel-row__line {
  height: 13px;
  border-radius: 6px;
}

.inbox-skel-row__line--name {
  width: 58%;
}

.inbox-skel-row__line--pill {
  width: 34%;
  height: 18px;
  border-radius: 999px;
}

@media (prefers-reduced-motion: reduce) {
  .inbox__li {
    animation: none;
  }
}
</style>
